// tutorial-real-board — Tutorial match host (CANVAS-DRIVEN, P1–P5 complete).
//
// Supersedes the Plan 08-06 simplified DOM mock. The host renders the REAL match
// board-stack + interactions + cinematics — the same components a live match
// uses — fed by the scripted tutorial runner's PlayerView, so design/animation
// changes map straight onto the tutorial (the goal in memory tutorial-real-board):
//
//   <MatchCanvas>           — Pixi 5-layer scene graph, the real visual surface.
//   <MatchGrid>             — the parallel-DOM a11y/keyboard/click surface.
//   <WordTraceLayer>        — the real pointer-drag trace ribbon + per-cell combat text.
//   <AnchorPlacementOverlay>/<AnchorPlacementPrompt> — the real anchor gate (P2).
//   <FlopReveal>            — the real T6 flop-growth cinematic (P3).
//   <EmpowerModal>          — the real role grid (empower step).
//   <WinOverlay>            — the real end-of-match celebration (P4).
//   <AffinityChipRow>/<PowerupTray> — the real teaching surfaces.
//
// All read state EXCLUSIVELY from `runner.getView()` (a full PlayerView, same
// wire shape a real match projects), and the interactive layers route their
// selection into the SAME `$turnBuilder` atom + window CustomEvents the live
// MatchScreen uses, driven to the runner (scripted validate + advance) instead
// of the network — DEC-021's single client-only exception (the runner imports
// zero net/* modules; grep-gated in runner.test.ts case 6).
//
// Phase map: P1 trace+submit gate · P2 anchor placement · P3 flop growth · P4
// capture-to-win + WinOverlay · P5 reward timing (trace token banked on submit).

import { useStore } from '@nanostores/react'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  BOARD_COLS,
  boardRows,
  centerGrowWindow,
} from '../../game/layers/board-layout.js'
import type { PlayerView } from '../../game/PlayerViewSchema.js'
import {
  createTutorialRunner,
  type TutorialRunner,
} from '../../game/tutorial/runner.js'
import { TUTORIAL_SCRIPT } from '../../game/tutorial/script.js'
import type { TutorialStep } from '../../game/tutorial/types.js'
import { $boardReanchor } from '../../stores/board-reanchor.js'
import { $reducedMotion } from '../../stores/motion.js'
import {
  $turnBuilder,
  clearTurnBuilder,
  setPhaseA,
} from '../../stores/turnBuilder.js'
import { HINT_REGISTRY } from '../coach/HintRegistry.js'
import { Btn } from '../components/Btn'
import { useAnnounce } from '../components/useAnnounce.js'
import { AffinityChipRow } from '../match/AffinityChipRow.js'
import { AnchorPlacementOverlay } from '../match/AnchorPlacementOverlay.js'
import { AnchorPlacementPrompt } from '../match/AnchorPlacementPrompt.js'
import { EmpowerModal } from '../match/EmpowerModal.js'
import { FlopReveal } from '../match/FlopReveal.js'
import { resolveGrowRows } from '../match/grow-rows.js'
import { MatchCanvas } from '../match/MatchCanvas.js'
import { MatchGrid } from '../match/MatchGrid.js'
import { PowerupTray } from '../match/PowerupTray.js'
import { WinOverlay } from '../match/WinOverlay.js'
import { WordTraceLayer } from '../match/WordTraceLayer.js'

interface TutorialMatchHostProps {
  /** Fires when the runner completes (after the final script step). */
  onComplete: () => void
  /**
   * FLOW-07 — reports the player's true interactive progress so the wrapper's
   * progress dots stay in lockstep with the host (instead of a dead counter).
   * index is 0-based among the player-interactive steps; total is their count.
   */
  onStepChange?: (interactiveIndex: number, interactiveTotal: number) => void
}

/** Player-interactive steps (the ones the player clicks through). */
const INTERACTIVE_STEP_COUNT = TUTORIAL_SCRIPT.filter(
  (s) => s.kind === 'player_turn',
).length

// The tutorial's first interactive step teaches the core drag-to-trace gesture.
// The canonical first word is CAT (game/tutorial/script.ts seeds CATBIN so
// CAT/BAT/TAB are all winnable; the coach hint is "Drag across letters to
// spell" and the CTA is "Spell CAT").
const TUTORIAL_TRACE_WORD = 'CAT'

/**
 * A11Y-01 — resolve the i18n message id for the step's header description.
 * Player-turn steps key off their coach copy (player_hint_key →
 * onboarding.coach.*, present in en/sv).
 */
function stepDescriptionId(step: TutorialStep | null): string {
  if (!step) return 'tutorial.step.complete'
  if (step.kind === 'reveal' && step.reveal === 't1')
    return 'tutorial.step.ready'
  if (step.kind === 'reveal' && step.reveal === 't6')
    return 'tutorial.step.flop'
  if (step.kind === 'apprentice_turn') return 'tutorial.step.apprentice'
  if (step.player_hint_key) return step.player_hint_key
  return 'tutorial.step.complete'
}

/**
 * The player's own lettered cells (cell_idx order): the trace source row. Used by
 * the CTA "Spell CAT" fallback (keyboard/SR/walker) to stage the canonical word.
 * Anchor cells are INCLUDED — an Anchor carries its letter and stays traceable
 * (buildLetterMap excludes only Hollows), so by the final capture turn the
 * player's CAT runes are spellable even if one of them now holds their Anchor.
 */
function seedCells(
  view: PlayerView,
): ReadonlyArray<{ idx: number; letter: string }> {
  return view.board
    .filter((c) => c.tint === 'a' && c.letter !== null && !c.is_hollow)
    .map((c) => ({ idx: c.cell_idx, letter: (c.letter ?? '').toUpperCase() }))
}

/**
 * Greedy left-to-right match of `targetWord` against the seeded cells, returning
 * the CELL INDICES that spell it (e.g. CAT in CATBIN → [0,1,2]). [] if
 * unspellable. This is the keyboard/SR/click fallback for the trace step: it
 * stages the same `$turnBuilder` Phase A path a real pointer drag would, so a
 * non-pointer user (and the jsdom regression test) can complete the trace.
 */
function traceCellsForWord(
  cells: ReadonlyArray<{ idx: number; letter: string }>,
  targetWord: string,
): number[] {
  const out: number[] = []
  const used = new Set<number>()
  for (const ch of targetWord.toUpperCase()) {
    let found = -1
    for (const c of cells) {
      if (c.letter === ch && !used.has(c.idx)) {
        found = c.idx
        break
      }
    }
    if (found < 0) return []
    out.push(found)
    used.add(found)
  }
  return out
}

export function TutorialMatchHost({
  onComplete,
  onStepChange,
}: TutorialMatchHostProps): JSX.Element {
  const intl = useIntl()
  // Live $turnBuilder — the SAME compose buffer the live MatchScreen reads. The
  // trace step seeds it to Phase A 'word'; WordTraceLayer (pointer) + MatchGrid
  // (click/keyboard) both write the traced path here, and the submit gate reads
  // the staged word back out.
  const turn = useStore($turnBuilder)
  // Reduced-motion gate + the board-reanchor "floor-drop" epoch — both consumed
  // by the T6 flop grow animation (resolveGrowRows + the reused FlopReveal).
  const reducedMotion = useStore($reducedMotion)
  const boardReanchorEpoch = useStore($boardReanchor)
  // Index of the next step to play (cursor into TUTORIAL_SCRIPT).
  const [stepIndex, setStepIndex] = useState(0)
  // Most recently advanced-into step (for description).
  const [currentStep, setCurrentStep] = useState<TutorialStep | null>(null)
  const [view, setView] = useState(() => {
    // Initial PlayerView is held by the runner; expose it for first paint.
    const tmp = createTutorialRunner({
      onPlayerView: () => undefined,
      onComplete: () => undefined,
      onCoachHint: () => undefined,
    })
    return tmp.getView()
  })
  const [completed, setCompleted] = useState(false)
  // ANCHOR-03 (P2) — whether the player has placed their Anchor on the current
  // anchor step. Drives the requires_anchor_placement gate + the CTA two-press
  // (Place anchor → Continue). Reset on advance so a later anchor step (none in
  // v1, but future-proof) starts fresh.
  const [anchorPlaced, setAnchorPlaced] = useState(false)
  // P3 — whether the T6 flop cinematic has played out on the current reveal step
  // (so the FlopReveal overlay mounts once and dismisses to the grown board).
  // Reset on advance.
  const [flopDismissed, setFlopDismissed] = useState(false)
  // P4 — whether the player has dismissed the end-of-match WinOverlay (via
  // HOME/REPLAY); set together with the onComplete → signup handoff so the
  // celebration overlay unmounts instead of lingering under the signup prompt.
  const [winDismissed, setWinDismissed] = useState(false)
  const runnerRef = useRef<TutorialRunner | null>(null)

  // Build the runner ONCE; advance() drives the state forward.
  useEffect(() => {
    const runner = createTutorialRunner({
      onPlayerView: (v) => setView(v),
      onComplete: () => {
        setCompleted(true)
      },
      onCoachHint: () => undefined,
    })
    runnerRef.current = runner
  }, [])

  // Auto-advance the opening reveal(s) so the player lands DIRECTLY on the
  // first INTERACTIVE step — the draggable "spell CAT" trace — instead of a
  // separate "Continue" gate. The player can then drag the seed runes
  // immediately (matching the coach hint "Drag across letters to spell"). The
  // loop-based walkTutorialToSignup e2e helper advances until the signup prompt,
  // so it is robust to the dropped gate.
  useEffect(() => {
    if (stepIndex !== 0 || !runnerRef.current) return
    const runner = runnerRef.current
    const firstPlayerIdx = TUTORIAL_SCRIPT.findIndex(
      (s) => s.kind === 'player_turn',
    )
    const landIdx = firstPlayerIdx >= 0 ? firstPlayerIdx : 0
    // Display cadence (APPLY-THEN-SHOW): showing TUTORIAL_SCRIPT[k] follows k+1
    // runner advances, so the board always reflects step k's OUTCOME while step
    // k's coach copy is shown. This is correct for the reveal/apprentice/flop
    // steps — the apprentice's anchor and the T6 flop's six runes must be
    // visible while their coach explains them (a `< landIdx` bound would defer
    // those mutations a step, surfacing the flop on the capture step). The
    // trace_word step here is a no-op signpost (P5): its +1 token is banked
    // out-of-band on the player's submit (runner.awardTraceWord), so the token
    // HUD reads 0 on the trace step without disturbing the reveal cadence.
    for (let i = 0; i <= landIdx; i += 1) runner.advance()
    setCurrentStep(TUTORIAL_SCRIPT[landIdx] ?? null)
    setStepIndex(landIdx + 1)
  }, [stepIndex])

  // The first interactive step (trace_word) lets the player DRAG across the
  // real board's seed-rune cells to spell the word — teaching the core gesture.
  // Tracing STAGES the word into $turnBuilder (does not advance); the player
  // then presses "Submit turn".
  const isTraceStep =
    !completed &&
    currentStep?.kind === 'player_turn' &&
    currentStep.expected_player_action === 'trace_word'

  // ANCHOR-03 (P2) — the anchor step opens the REAL placement gate on the board
  // (AnchorPlacementOverlay Sun-Yellow eligible cells + AnchorPlacementPrompt +
  // MatchGrid tap interception) until the player taps a cell.
  const isAnchorStep =
    !completed &&
    currentStep?.kind === 'player_turn' &&
    currentStep.expected_player_action === 'place_anchor'

  // P3 — the T6 flop reveal step. The runner has already GROWN the board 36→48
  // (apply-then-show); the reused FlopReveal cinematic plays over it, pulses the
  // board-reanchor epoch to release the height grow + the LetterLayer cascade.
  const isFlopStep =
    !completed && currentStep?.kind === 'reveal' && currentStep.reveal === 't6'

  // P4 — the final capture turn ends the tutorial on a TRACE-to-win: the player
  // traces a word on the grown board, and submitting it consumes the Apprentice's
  // Anchor (runner capture step) → win → the real WinOverlay. It reuses the exact
  // trace machinery as the first step, so `isWordStep` gates both.
  const isCaptureStep =
    !completed &&
    currentStep?.kind === 'player_turn' &&
    currentStep.expected_player_action === 'capture'
  const isWordStep = isTraceStep || isCaptureStep

  // The PlayerView fed to the board stack. Identical to the runner's view
  // except on the anchor step, where we OPEN the placement gate
  // (requires_anchor_placement) until the player has placed — the runner's view
  // carries no gate (it is a host/presentation concern), and placeAnchorAt
  // persists the chosen cell's brand into the authoritative view. Memoized so
  // the @pixi/react-memoized MatchCanvas only re-renders when the view, the
  // step, or the placed-state actually change (not on every $turnBuilder draft).
  const boardViewData = useMemo(
    () =>
      isAnchorStep
        ? { ...view, requires_anchor_placement: !anchorPlaced }
        : view,
    [view, isAnchorStep, anchorPlaced],
  )

  const minWordLength = view.config?.minWordLength ?? 3
  const stagedWord =
    isWordStep && turn.phaseA?.kind === 'word' ? turn.phaseA.word : ''
  // The trace is "staged" (gate satisfied) once a long-enough word is composed.
  const traceStaged = isWordStep && stagedWord.length >= minWordLength

  // Seed Phase A to 'word' the moment a word step (trace or capture) becomes
  // active, so the board's interactive layers come alive: MatchGrid cell
  // clicks/Enter build a path (its handler requires phaseA.kind==='word'), and
  // WordTraceLayer paints the live ribbon. Runs only when isWordStep TOGGLES, so
  // a staged word is never wiped mid-step.
  useEffect(() => {
    if (isWordStep) {
      setPhaseA({ kind: 'word', word: '', path: [] })
    }
  }, [isWordStep])

  // Never leak a staged turn-builder draft into the next screen / test suite.
  useEffect(() => clearTurnBuilder, [])

  // NOTE: we deliberately do NOT reset $boardReanchor. The epoch is MONOTONIC by
  // contract (stores/board-reanchor.ts) — resetBoardReanchor is test-only — and
  // the design is self-healing: handledEpochRef (below) captures the LIVE epoch
  // at first render, so the flop's pulse (epoch+1) always reads as strictly
  // greater and releases the grow, and a later match's cubes likewise capture
  // whatever value the tutorial left it at and glide only on the next pulse.

  const advanceStep = useCallback(() => {
    const runner = runnerRef.current
    if (!runner) return
    if (runner.isComplete()) {
      onComplete()
      return
    }
    const showStep = TUTORIAL_SCRIPT[stepIndex] ?? null
    // P4 — the FINAL capture turn is shown BEFORE its application (show-then-
    // apply, the lone exception to the apply-then-show cadence): advancing INTO
    // it leaves the runner un-advanced (completed stays false) so the player can
    // trace on the pre-capture board (the Apprentice's Anchor still present); the
    // capture (consume Anchor → win) is applied on the NEXT advance — its submit
    // — which completes the runner and raises the WinOverlay. Every other step
    // keeps apply-then-show (the board reflects step k while its coach shows).
    if (showStep?.expected_player_action !== 'capture') {
      runner.advance()
    }
    setCurrentStep(showStep)
    setStepIndex((i) => i + 1)
    // Leaving any step clears the compose buffer (the next word step re-seeds),
    // the anchor-placed latch (the next anchor step re-opens its gate), and the
    // flop-dismissed latch.
    clearTurnBuilder()
    setAnchorPlaced(false)
    setFlopDismissed(false)
  }, [onComplete, stepIndex])

  // ANCHOR-03 (P2) — route a placement (a MatchGrid eligible-cell tap fires the
  // runegambit:place-anchor CustomEvent; the AnchorPlacementOverlay onPlace DI
  // is the belt-and-suspenders path) to the runner instead of the network, then
  // latch placed so the gate closes + the CTA flips to Continue.
  const handleAnchorPlace = useCallback(
    (cellIdx: number) => {
      if (!isAnchorStep || anchorPlaced) return
      runnerRef.current?.placeAnchorAt(cellIdx)
      setAnchorPlaced(true)
    },
    [isAnchorStep, anchorPlaced],
  )

  // The DOM MatchGrid intercepts eligible-cell taps while the anchor gate is
  // open and dispatches this window CustomEvent (the Pixi overlay sits beneath
  // the pointer-events:none canvas, so its own onPointerDown never fires) — the
  // same seam MatchScreen wires to the network. Here it drives the runner.
  useEffect(() => {
    function onPlaceAnchorRequest(e: Event): void {
      const detail = (e as CustomEvent<{ cell_idx: number }>).detail
      if (!detail || typeof detail.cell_idx !== 'number') return
      handleAnchorPlace(detail.cell_idx)
    }
    window.addEventListener('runegambit:place-anchor', onPlaceAnchorRequest)
    return () =>
      window.removeEventListener(
        'runegambit:place-anchor',
        onPlaceAnchorRequest,
      )
  }, [handleAnchorPlace])

  // P4 — on completion we show the real WinOverlay (the celebration) instead of
  // firing onComplete immediately; the WinOverlay's HOME/REPLAY buttons (and the
  // completed-state CTA fallback the e2e walker force-clicks) call onComplete,
  // which routes to the signup soft-prompt. So there is NO auto-onComplete effect.

  // WordTraceLayer onComplete (pointer lift) — recompute the word from the final
  // path and commit it to $turnBuilder, mirroring MatchScreen.onWordTraceComplete
  // so the staged word the submit gate reads is exactly what was traced.
  const onTraceComplete = useCallback(
    (path: number[]) => {
      const letterByCell = new Map<number, string>()
      for (const cell of view.board) {
        if (cell.letter !== null && !cell.is_hollow) {
          letterByCell.set(cell.cell_idx, cell.letter)
        }
      }
      const word = path.map((i) => letterByCell.get(i) ?? '').join('')
      setPhaseA({ kind: 'word', word, path })
    },
    [view.board],
  )

  // P4 — proceed past the win: dismiss the WinOverlay AND fire onComplete (which
  // routes to the signup soft-prompt) so the celebration never lingers under the
  // signup. Reached from the WinOverlay HOME/REPLAY buttons and the completed-
  // state CTA fallback the e2e walker force-clicks.
  const handleWinProceed = useCallback(() => {
    setWinDismissed(true)
    onComplete()
  }, [onComplete])

  const ctaLabel = useMemo(() => {
    const CTA_BY_ACTION: Record<string, string> = {
      trace_word: 'tutorial.cta.spell_cat',
      empower: 'tutorial.cta.empower_knight',
      capture: 'tutorial.cta.capture_king',
      place_anchor: 'tutorial.cta.place_anchor',
      tap_continue: 'tutorial.cta.got_it',
      inspect_powerups: 'tutorial.cta.got_it',
    }
    const id =
      !currentStep || currentStep.kind === 'reveal'
        ? 'tutorial.cta.continue'
        : (CTA_BY_ACTION[currentStep.expected_player_action ?? ''] ??
          'tutorial.cta.continue')
    return intl.formatMessage({ id })
  }, [currentStep, intl])

  // CTA on the trace step: first STAGE the word (a real pointer drag stages it
  // directly; this button is the keyboard/SR/click fallback), THEN SUBMIT to
  // advance — mirrors the real game's trace → SUBMIT TURN. Other steps advance.
  const handleCta = useCallback(() => {
    if (completed) {
      handleWinProceed()
      return
    }
    if (isWordStep && !traceStaged) {
      // Stage the canonical word by seeding the same Phase A path a drag would
      // (the keyboard/SR/walker fallback for both the trace step and the final
      // capture step — the player's CAT runes survive into the capture turn).
      const path = traceCellsForWord(seedCells(view), TUTORIAL_TRACE_WORD)
      const word = path.length > 0 ? TUTORIAL_TRACE_WORD : ''
      setPhaseA({ kind: 'word', word, path })
      return
    }
    if (isAnchorStep && !anchorPlaced) {
      // Keyboard/SR/walker fallback for the real Sun-Yellow cell tap: place on
      // the first eligible cell (does not advance — the player then Continues).
      const firstEligible = boardViewData.board.find(
        (c) => !c.is_anchor && !c.is_hollow && c.letter !== null,
      )
      if (firstEligible) handleAnchorPlace(firstEligible.cell_idx)
      return
    }
    // P5 — submitting the FIRST trace banks the +1 token (the trace_word step is
    // a no-op signpost), so the reward lands on the player's action rather than
    // pre-applied in auto-land. Trace step only — the capture submit wins, it
    // doesn't bank a token.
    if (isTraceStep) {
      runnerRef.current?.awardTraceWord(stagedWord)
    }
    advanceStep()
  }, [
    completed,
    isTraceStep,
    isWordStep,
    traceStaged,
    stagedWord,
    isAnchorStep,
    anchorPlaced,
    boardViewData,
    handleAnchorPlace,
    handleWinProceed,
    view,
    advanceStep,
  ])

  const description = intl.formatMessage({ id: stepDescriptionId(currentStep) })
  // Step kinds that surface a dedicated real component (which carries its own
  // instruction copy / teaching content).
  const isAffinityStep = currentStep?.spotlight_target === 'affinity_strip'
  const isPowerupStep = currentStep?.spotlight_target === 'powerup_loadout'
  const isEmpowerStep = currentStep?.expected_player_action === 'empower'
  // ONE coach line (the step's copy), shown ABOVE the board. Suppressed on the
  // steps whose REAL overlay already carries the instruction — the anchor prompt
  // banner, the flop cinematic, the empower modal — and on completion (the
  // WinOverlay), so the same copy never appears twice on screen.
  const showCoach = !completed && !isAnchorStep && !isFlopStep && !isEmpowerStep
  void HINT_REGISTRY

  // P3 — animatable --grow-rows (mirrors MatchScreen's grow-rows logic). The
  // LIVE row count is boardRows(view.board.length) (6 pre-flop, 8 post-flop).
  // While the FlopReveal cinematic is in flight the wrapper holds at 6 rows
  // behind the scrim, then animates to 8 when the reveal pulses the board-reanchor
  // "floor-drop" beat (resolveGrowRows). Reduced motion snaps immediately. If the
  // cinematic ever finishes WITHOUT pulsing the epoch (e.g. tap-skipped before
  // its beat), the safety-net snap brings the box to 8 once the reveal is idle so
  // the tutorial back-rank cells never paint off-screen (ARCA-154 / GH #94).
  // growCinematicInFlight is derived synchronously from the runner step
  // (isFlopStep) + the dismiss flag — no race, unlike the live match's setReveal.
  const boardRowCount = boardRows(view.board.length)
  const growCinematicInFlight = isFlopStep && !flopDismissed
  const [growRows, setGrowRows] = useState(boardRowCount)
  const handledEpochRef = useRef(boardReanchorEpoch)
  useEffect(() => {
    const { nextGrowRows, nextHandledEpoch } = resolveGrowRows({
      boardRowCount,
      growRows,
      prevEpoch: handledEpochRef.current,
      newEpoch: boardReanchorEpoch,
      reducedMotion,
      growCinematicInFlight,
    })
    if (nextGrowRows !== growRows) setGrowRows(nextGrowRows)
    handledEpochRef.current = nextHandledEpoch
  }, [
    boardRowCount,
    boardReanchorEpoch,
    reducedMotion,
    growRows,
    growCinematicInFlight,
  ])

  // ANCHOR-03 (P2) — the REAL AnchorPlacementOverlay (Pixi Sun-Yellow eligible-
  // cell pulse) rides the MatchCanvas overlayLayer slot so it lives in the same
  // World coordinate system as the letter cubes, exactly like the live match.
  // It self-gates on requires_anchor_placement; onPlace routes to the runner
  // (DEC-021 — never the network). Memoized so MatchCanvas's @pixi/react memo
  // holds across unrelated re-renders.
  const overlayLayer = useMemo(
    () =>
      isAnchorStep ? (
        <AnchorPlacementOverlay
          playerView={boardViewData}
          matchId={view.matchId}
          yourSlot={0}
          onPlace={handleAnchorPlace}
        />
      ) : undefined,
    [isAnchorStep, boardViewData, view.matchId, handleAnchorPlace],
  )

  // Phase 18 Plan 04 (POLISH-07) — coach-hint context announcement, routed
  // through the global polite <LiveRegion>. Re-fires when the description changes.
  const announce = useAnnounce()
  useEffect(() => {
    if (description) {
      announce(description, 'polite')
    }
  }, [description, announce])

  // FLOW-07 — report interactive progress so the wrapper's dots track the host.
  useEffect(() => {
    if (!onStepChange) return
    const curIdx = currentStep ? TUTORIAL_SCRIPT.indexOf(currentStep) : -1
    const seen = TUTORIAL_SCRIPT.slice(0, curIdx + 1).filter(
      (s) => s.kind === 'player_turn',
    ).length
    onStepChange(Math.max(0, seen - 1), INTERACTIVE_STEP_COUNT)
  }, [currentStep, onStepChange])

  // Phase 18 Plan 07 Task 1 — POLISH-10: the outer <main id="main"> lives on
  // TutorialScreen. Render the host as a <section> so jest-axe's landmark rules
  // pass. data-tutorial-* + data-screen-region live on this section.
  return (
    <section
      data-tutorial-step={stepIndex}
      data-tutorial-completed={completed ? 'true' : 'false'}
      aria-label={intl.formatMessage({ id: 'tutorial.runner_section_aria' })}
      style={{
        // Size to CONTENT — the wrapper <main rg-screen> owns the 100vh + the
        // gradient background, so the progress dots sit right below the content
        // instead of being pushed down by a full-height section (the old big
        // empty gap). The flop/win scrims live in their own position:fixed
        // wrappers (below) so they still cover the whole screen.
        padding: 'var(--space-4) var(--space-4) var(--space-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        alignItems: 'center',
      }}
    >
      {/* The ONE coach line — the step's copy, shown above the board. Suppressed
          where a real overlay already teaches (anchor prompt / flop cinematic /
          empower modal) and on completion, so the copy never duplicates. A plain
          styled box (NOT an <aside> landmark — that trips axe's nested-landmark
          rule; the copy is also announced via the polite LiveRegion). */}
      {showCoach ? (
        <div
          data-testid="tutorial-coach-hint"
          data-tutorial-description=""
          style={{
            backgroundColor: 'var(--color-card)',
            border: '3px solid var(--color-cta-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3) var(--space-5)',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 600,
            fontSize: 'var(--font-size-16)',
            lineHeight: 1.4,
            boxShadow:
              '0 4px 0 color-mix(in srgb, var(--color-ink) 14%, transparent), var(--shadow-card)',
          }}
        >
          {description}
        </div>
      ) : null}

      {/* The REAL board-stack — same components a live match renders. The Pixi
          canvas is the visual surface (pointer-events:none so DOM events fall
          through); MatchGrid is the a11y/click surface; WordTraceLayer captures
          the pointer-drag trace (enabled only on the trace step). All three are
          fed by the runner's PlayerView. Unmounted on completion — the match is
          over and the WinOverlay / signup own the screen. */}
      {!completed ? (
        <div
          data-screen-region="tutorial-board-stack"
          style={{
            position: 'relative',
            ['--grow-rows' as string]: growRows,
            width: '100%',
            maxWidth: 384,
            aspectRatio: `calc(${BOARD_COLS} / var(--grow-rows, 6))`,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'grid',
            placeItems: 'center',
            // P3 — animate the height as --grow-rows transitions 6→8 at the flop.
            // The @property --grow-rows is registered globally (pieces.css) so the
            // inline transition on the registered <number> custom prop animates;
            // we keep our own region (tutorial-board-stack) rather than reuse the
            // match's "board-stack" (which a center-grow e2e spec targets), and
            // gate on reduced motion ourselves (the global CSS rule is scoped to
            // the match region).
            transition: reducedMotion
              ? undefined
              : '--grow-rows 500ms cubic-bezier(0, 0, 0.2, 1)',
          }}
        >
          <div
            style={{ pointerEvents: 'none', position: 'absolute', inset: 0 }}
          >
            <MatchCanvas
              playerView={boardViewData}
              boardView="letters"
              {...(overlayLayer !== undefined ? { overlayLayer } : {})}
            />
          </div>
          {/* MatchGrid is the a11y/keyboard/click layer; the trace renders ABOVE
            it so pointer-drag is captured before the cell buttons. The trace
            layer falls to pointer-events:none off the trace step, so MatchGrid
            stays inert (phaseA is cleared between steps). On the anchor step
            MatchGrid intercepts eligible-cell taps (requires_anchor_placement)
            and fires the runegambit:place-anchor CustomEvent the host listens for. */}
          <MatchGrid playerView={boardViewData} boardView="letters" />
          <WordTraceLayer
            playerView={boardViewData}
            onComplete={onTraceComplete}
            enabled={isWordStep}
          />
          {/* ANCHOR-01/05 — the REAL placement prompt banner (what to do + the
            Hollow trade-off). Self-gates on requires_anchor_placement; renders
            above the board, pointer-events:none so taps reach the gate beneath. */}
          <AnchorPlacementPrompt
            requiresAnchorPlacement={
              boardViewData.requires_anchor_placement === true
            }
            eligibleCount={
              boardViewData.requires_anchor_placement === true
                ? boardViewData.board.filter(
                    (c) => !c.is_anchor && !c.is_hollow && c.letter !== null,
                  ).length
                : 0
            }
          />
        </div>
      ) : null}

      {/* Staged-word readout — mirrors the live game's word/score before SUBMIT
          TURN; shown once the player has traced a long-enough word. Trace step
          only: the "+1 token" reward copy is wrong for the final capture turn
          (which wins the match rather than banking a token). */}
      {traceStaged && isTraceStep ? (
        <p
          data-testid="tutorial-staged-word"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-16)',
            color: 'var(--color-player-a)',
          }}
        >
          <FormattedMessage
            id="tutorial.trace.spelled"
            defaultMessage="Spelled {word} · +1 token"
            values={{ word: stagedWord.toUpperCase() }}
          />
        </p>
      ) : null}

      {/* Per-step rich teaching surface, REUSED from the live match: the affinity
          chip row (affinity step) and the power-up tray (power-ups step), in a
          folk card. The board's HUD already shows tokens/role, so the panel no
          longer repeats a token line; the other steps teach on the board + the
          coach line above. */}
      {!completed && (isAffinityStep || isPowerupStep) ? (
        <div
          data-testid="tutorial-teaching-panel"
          style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--space-3) var(--space-4)',
            margin: '0 auto',
            width: '100%',
            maxWidth: '480px',
          }}
        >
          {isAffinityStep ? <AffinityChipRow playerView={view} /> : null}
          {isPowerupStep ? (
            <PowerupTray
              playerView={view}
              loadout={view.your_powerup_loadout}
              onSelect={() => undefined}
            />
          ) : null}
        </div>
      ) : null}

      {/* EMPOWER-02 — mount the REAL EmpowerModal on the empower step (no
          bespoke tutorial copy of the role grid). It reads the runner's
          role_costs + token balance + claimed-word length, so the player sees
          the actual roles, costs, and affordability gating. Confirm/close both
          advance the guided flow (the runner applies the Knight role). */}
      {currentStep?.expected_player_action === 'empower' && !completed ? (
        <EmpowerModal
          roleCosts={view.role_costs}
          // Teaching state: the runner applies the empower on arrival at this
          // step, so present the PRE-spend balance (1 token, the just-traced
          // 3-letter word) so Knight/Bishop read as affordable.
          tokenBalance={1}
          wordLength={view.your_words_played[0]?.length ?? 3}
          initialSelectedRole="knight"
          onConfirm={() => advanceStep()}
          onClose={() => advanceStep()}
        />
      ) : null}

      {/* The advance CTA. Hidden on completion — the win is over, and the real
          WinOverlay's HOME/REPLAY buttons own the route to the signup prompt (so
          there is no dead CTA buried under the celebration scrim). */}
      {!completed ? (
        // Folk parity: the tutorial's Next/Play CTA is the Ember "primary"
        // button (prototype Btn kind="primary"), not the gold accent.
        <Btn
          kind="primary"
          size="lg"
          onClick={handleCta}
          dataCta="tutorial-advance"
          testId="tutorial-continue"
          style={{ minWidth: '240px' }}
        >
          {traceStaged
            ? intl.formatMessage({ id: 'tutorial.cta.submit_turn' })
            : isAnchorStep && anchorPlaced
              ? intl.formatMessage({ id: 'tutorial.cta.continue' })
              : ctaLabel}
        </Btn>
      ) : null}

      {/* P3 — the REAL "THE FLOP" cinematic. The runner has already grown the
          board 36→48; this overlay scrim covers the board while it holds at 6
          rows, then its FSM pulses $boardReanchor (~t=900ms) to release the
          height grow + the LetterLayer new-rune cascade in sync. onComplete only
          DISMISSES the cinematic (the board stays grown) — the Continue CTA owns
          the advance, so the loop-based e2e walker still advances this step in a
          single click. */}
      {isFlopStep && !flopDismissed ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            pointerEvents: 'none',
          }}
        >
          <FlopReveal
            newCellIndices={(() => {
              const w = centerGrowWindow(view.board.length)
              return w
                ? Array.from({ length: w.end - w.start }, (_, i) => w.start + i)
                : []
            })()}
            onComplete={() => setFlopDismissed(true)}
          />
        </div>
      ) : null}

      {/* P4 — the REAL end-of-match WinOverlay celebration when the player wins
          (the final capture turn consumed the Apprentice's Anchor). It does NOT
          auto-dismiss; HOME/REPLAY both route to onComplete → the signup soft-
          prompt (the tutorial has no replay surface, so REPLAY also proceeds).
          The completed-state Continue CTA behind the scrim is the e2e walker's
          force-click fallback to the same onComplete. */}
      {completed && !winDismissed ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
          <WinOverlay
            outcome="win"
            onHome={handleWinProceed}
            onReplay={handleWinProceed}
            rewards={{ xp: 50 }}
            streakCount={0}
            yourSlot={0}
            yourAffinityLetters={view.your_affinity_letters}
          />
        </div>
      ) : null}
    </section>
  )
}
